<script>
    import { onMount } from "svelte";

    import { allGroups, isMobile } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        Button,
        CollapsibleTableIcons,
        PostCsvFileModal,
        SearchSelect,
        LoadingModal,
        NotificationModal,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
        SendQuoteOrInvoiceToPatientModal,
    } from "../../Components/Common/index.svelte";

    import { user } from "../../stores";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        DirectInsuranceType,
        invoiceStatus,
    } from "../../Utils/utilEnums.svelte";

    import {
        getAmountInCurrency,
        goToURL,
        replaceURL,
        getPaymentAmountPaid,
        getFormatDateAndTime,
        getFormatDate,
        getDisplayTotal,
        showError,
        showException,
        formatPortalUrls,
        goToDownloadPage,
        getInvoiceStatus,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        openPortalPage,
        isDateAndTimeInPast,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import DecimalInput from "../../Components/Common/DecimalInput.svelte";
    import {
        EmptyGroupDetails,
        MaxPaperProcessingFee,
    } from "../../Utils/utilConstants.svelte";
    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    export let params = {};

    let isGettingInvoices = false,
        returnInvoicesArr = [],
        filters = { status: "1,2" },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedInvoices = [],
        selectedInvoicesId = [],
        isPostCSVModalOpen = false,
        isDownloadingInvoice = false,
        isRegeneratingInvoice = false,
        isSetInvoicePaperModalOpen = false,
        selectedInvoiceID = Number(0),
        isSettingInvoicePaper = false,
        showFilters = false;

    let selectedDoctors = [];

    let sendInvoiceToPatientModalOpen = false,
        selectedInvoiceData = {};

    $: columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center ml-3 rounded-tl-xl",
            renderValue: (v) =>
                `<input type="checkbox" class="checkbox checkbox-primary checkbox-xs ml-3 text-white cursor-pointer" name="selectCheckBox" id="selectCheckBoxId${v.id}">`,
            csvExportHidden: true,
        },
        {
            key: "invoiceNumber",
            title: "Invoice Number",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getInvoiceNumber(v.invoiceNumber),
        },
        {
            key: "encounterId",
            title: "Enc. ID",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getEncounterID(v.patientEncounterId),
            csvContent: (v) => _getEncounterID(v.patientEncounterId, true),
        },
        {
            key: "doctorName",
            title: "Doctor Name",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                `<div class="cursor-pointer hover:underline w-[98px]">${
                    v.doctorName ?? "-"
                }</div>`,
            csvContent: (v) => `${v.doctorName}`,
            csvExportHidden: !isUserEnableViewDoctor,
            isHidden: !isUserEnableViewDoctor,
        },
        {
            key: "patientName",
            title: "Patient Name",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getPatientName(v.billingDetails),
        },
        {
            key: "createdDate",
            title: "Create Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDate(v.createdDate, v.isExpired),
        },
        {
            key: "dueDate",
            title: "Due Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDate(v.dueDate, v.isExpired, true),
        },
        // {
        //     key: "quoteApprove",
        //     title: "Approved Date",
        //     headerClass: "text-left px-3 cursor-default",
        //     class: "text-left px-3 cursor-pointer",
        //     renderValue: (v) =>
        //         _getQuoteApproveDate(v.quoteApprovedDate, v.quoteApproved),
        // },
        {
            key: "gapFee",
            title: "Gap Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.actualGap),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.rebate),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(
                    v.addGST ? (v.actualGap + v.rebate) * 0.1 : 0,
                ),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.internalProcessingFee ?? 0),
            headerConfig: {
                showTooltip: true,
                tooltipContent: "Internal Processing Fee",
            },
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getDisplayTotal(v),
        },
        {
            key: "payments",
            title: "Payments",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getPaymentAmountPaid(v.payments),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
            csvContent: (v) => _getStatus(v.status, true),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: [
                        "viewPatientEncounterIcon",
                        "viewPatientIcon",
                        "viewDoctorIcon",
                        "downloadLinkIcon",
                        "printIcon",
                        "downloadIcon",
                        "reGenerateIcon",
                        "sendToPatientIcon",
                        "downloadHealthIcon",
                        "goToPortalPageIcon",
                    ],
                    tableType: "Invoice",
                },
            },
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = [
        "id",
        "invoiceNumber",
        "dueDate",
        "createdDate",
    ];

    $: isUserAdmin = $user.role == 100;

    $: isUserEnableViewDoctor = $user.role >= 20;

    $: _getCheckboxString(selectedInvoicesId);
    $: if (selectedInvoices.length >= 0 && returnInvoicesArr.length > 0) {
        selectedInvoicesId = selectedInvoices.map((invoice) => invoice.id);
        _updateHeaderIcon();
    }

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadInvoices(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.invoiceNumber = arr[i].split("=")[1].toString();

                if (arr[i].includes("id=")) filters.id = arr[i].split("=")[1];

                if (arr[i].includes("patientEncounterID="))
                    filters.patientEncounterID = arr[i]
                        .split("=")[1]
                        .toString();

                if (arr[i].includes("status="))
                    filters.status = isNaN(arr[i].split("=")[1])
                        ? arr[i].split("=")[1]
                        : Number(arr[i].split("=")[1]);

                if (arr[i].includes("doctorId=") && isUserEnableViewDoctor) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }
            }
            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (!selectedInvoices.some((p) => p.id == Number(ca))) {
                            let newInvoiceItem = { id: Number(ca) };
                            selectedInvoices = [
                                ...selectedInvoices,
                                newInvoiceItem,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadInvoices = (start, limit, params) => {
        isGettingInvoices = true;
        if (params.sort) params.sort = params.sort.toLowerCase();
        if (params["filter[id]"]) {
            params = {
                "filter[id]": params["filter[id]"],
            };
        }
        apiMethods.getMethod
            .getInvoices(start, limit, params)
            .then((res) => _handleInvoices(res))
            .then(() => _updateHeaderSortIcon());
    };

    const setInvoicePaper = async () => {
        isSettingInvoicePaper = true;

        await apiMethods.putMethod
            .sendPaperInvoice(selectedInvoiceID, paperProcessingFee)
            .then((res) => _handleSendPaperInvoice(res))
            .catch((err) => showException(err));

        closeSetInvoicePaperModal();

        isSettingInvoicePaper = false;
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedInvoices = [];

        updateURL();

        loadInvoices(0, pageSize, _getSearchParams());
    };

    const handleSearchInvoiceNo = () => {
        filters.invoiceNumber = filters.invoiceNumber
            ? "INV" + filters.invoiceNumber.toString().replace(/inv/gi, "")
            : "";

        handleFilterSelect();
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedInvoices = [];
            _updateCheckBox();
        } else {
            returnInvoicesArr.forEach((invoice) => {
                if (!selectedInvoicesId.includes(invoice.id)) {
                    _addSelectedInvoice(invoice);
                }
            });
        }
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "selected?") _addSelectedInvoice(rowData);
        else if (key == "actions") {
            if (className.includes("viewDoctorIcon") && isUserEnableViewDoctor)
                goToURL(`/Account/view/${rowData.doctorId}`);
            else if (className.includes("viewPatientIcon"))
                goToURL(`/Patient/view/${rowData.patientId}`);
            else if (className.includes("viewPatientEncounterIcon"))
                goToURL(
                    `/PatientEncounter/view/${rowData.patientEncounterId}/tab=billing`,
                );
            else if (className.includes("downloadIcon"))
                downloadInvoice(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateInvoice(rowData);
            else if (className.includes("goToPortalPageIcon"))
                openPortalPage(rowData);
            else if (className.includes("downloadLinkIcon"))
                goToInvoice(rowData.id);
            else if (className.includes("sendToPatientIcon"))
                _openSendInvoiceToPatientModal(rowData);
            else if (className.includes("downloadHealthIcon"))
                _downloadHealthFundInvoice(rowData.id);
            else if (className.includes("printIcon"))
                openSetInvoicePaperModal(rowData);
        } else if (key == "encounterId")
            goToURL(`/PatientEncounter/view/${rowData.patientEncounterId}`);
        else if (key == "doctorName")
            goToURL(`/Account/view/${rowData.doctorId}`);
        else if (rowData.id) goToURL(`/Invoices/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadInvoices(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadInvoices(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadInvoices(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/Invoices/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/Invoices/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const downloadInvoice = (id, isRegenerate) => {
        goToDownloadPage(1, id, isRegenerate);
    };

    const _downloadHealthFundInvoice = async (id) => {
        isDownloadingInvoice = true;

        await apiMethods.postMethod
            .createInvoiceForHealthFund(id)
            .then((res) => {
                if (res.id) downloadInvoice(id);
                else
                    showError(
                        "Unable to download this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Unable to download this invoice file, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isDownloadingInvoice = false;
    };

    const regenerateInvoice = async (data) => {
        const { id, doctorId, patientId, patientEncounterId } = data;

        const regenerateObj = {
            type: 1,
            entityId: id,
            doctorId,
            patientId,
            patientEncounterId,
        };

        isRegeneratingInvoice = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadInvoice(id, true);
                else
                    showError(
                        "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingInvoice = false;
    };

    const goToInvoice = (id) => {
        // const url = `https://portal.tcihealth.com.au/?i=${id}`;
        const url = formatPortalUrls("i", id);
        window.open(url, "_blank");
    };

    const openPostCSVModal = () => {
        isPostCSVModalOpen = true;
    };

    const closePostCSVModal = () => {
        isPostCSVModalOpen = false;
    };

    const openSetInvoicePaperModal = async (row) => {
        hasInternalProcessingFee = false;
        estimateProcessingFee = 0;
        if (row.internalProcessingFee) {
            const encounterData =
                await apiMethods.getMethod.getPatientEncounterById(
                    row.patientEncounterId,
                );
            if (
                encounterData.estimates?.length &&
                encounterData.estimates[0].internalProcessingFee
            ) {
                estimateProcessingFee =
                    encounterData.estimates[0].internalProcessingFee;
                paperProcessingFee =
                    row.internalProcessingFee - estimateProcessingFee;
            } else {
                paperProcessingFee = row.internalProcessingFee;
            }
            hasInternalProcessingFee = paperProcessingFee > 0;
        } else if (row.groupId) {
            if (!$allGroups.length) await cacheAllGroups();
            const group = $allGroups.find((g) => g.id == row.groupId);
            paperProcessingFee =
                group?.paperProcessingFee ??
                EmptyGroupDetails.paperProcessingFee;
        } else paperProcessingFee = EmptyGroupDetails.paperProcessingFee;
        selectedInvoiceID = Number(row.id);
        isSetInvoicePaperModalOpen = true;
    };

    const closeSetInvoicePaperModal = () => {
        selectedInvoiceID = Number(0);
        isSetInvoicePaperModalOpen = false;
    };

    // HELPER FUNCTIONS
    const _handleInvoices = (res) => {
        isGettingInvoices = false;
        if (res.error) showError(res.error);
        else {
            returnInvoicesArr = [...res.results];
            count = res.total;
        }
        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _handleSendPaperInvoice = (res) => {
        if (!res || res.error)
            showError(
                "Failed to request paper copy of invoice, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.id) {
            downloadInvoice(res.id);
            loadInvoices(page * pageSize, pageSize, _getSearchParams());
        }
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedInvoices.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnInvoicesArr.every((invoice) =>
                selectedInvoicesId.includes(invoice.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _addSelectedInvoice = (data) => {
        if (selectedInvoices.some((invoice) => invoice.id === data.id)) {
            selectedInvoices = selectedInvoices.filter(
                (invoice) => invoice.id !== data.id,
            );
        } else {
            selectedInvoices = [...selectedInvoices, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
        }
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnInvoicesArr.forEach((invoice) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${invoice.id}`,
                );
                if (checkbox) {
                    if (selectedInvoicesId.includes(invoice.id)) {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = false;
                    }
                }
            });
            _updateHeaderIcon();
        }, 1);
    };

    const _getSearchParams = () => {
        let params = {};
        if (filters.sort) params.sort = filters.sort;
        if (filters.invoiceNumber) params.search = filters.invoiceNumber;

        if (filters.id) params["filter[id]"] = filters.id;

        if (filters.patientEncounterID)
            params["filter[patientEncounterID]"] = filters.patientEncounterID;

        if (filters.status) params["filter[status]"] = filters.status;

        if (filters.doctorId && isUserEnableViewDoctor)
            params["filter[doctorId]"] = filters.doctorId;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;

            if (params["filter[id]"]) str += `&id=${params["filter[id]"]}`;

            if (params["filter[patientEncounterID]"])
                str += `&patientEncounterID=${params["filter[patientEncounterID]"]}`;

            if (params["filter[status]"])
                str += `&status=${params["filter[status]"]}`;

            if (params["filter[doctorId]"] && isUserEnableViewDoctor)
                str += `&doctorId=${params["filter[doctorId]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumber" &&
                            row.childNodes[0].data == "Invoice Number") ||
                        (params.sort == "dueDate" &&
                            row.childNodes[0].data == "Due Date") ||
                        (params.sort == "createdDate" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumber" &&
                            row.childNodes[0].data == "Invoice Number") ||
                        (params.sort == "dueDateDesc" &&
                            row.childNodes[0].data == "Due Date") ||
                        (params.sort == "createdDateDesc" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getInvoiceNumber = (invoiceNumber) => {
        return `${invoiceNumber}`;
    };

    const _getEncounterID = (encounterId, isCsvContent = false) =>
        isCsvContent
            ? `${encounterId}`
            : `<div class="cursor-pointer hover:underline">${encounterId}</div>`;

    const _getPatientName = (billingDetails) => {
        if (billingDetails.name) return `${billingDetails.name}`;
        else return "Missing patient name...";
    };

    const _getDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    // const _getQuoteApproveDate = (date, isApproved) => {
    //     if (isApproved) return getFormatDate(date);
    //     return "Not approved";
    // };

    const _getStatus = (status, isCsvContent = false) => {
        let statusText, statusColor;

        statusText = getInvoiceStatus(status);

        if (isCsvContent) return statusText;

        switch (status) {
            case 1:
            case 2:
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const _openSendInvoiceToPatientModal = async (data) => {
        const patientEncounterDetails = await apiMethods.getMethod.getPatientEncounterById(
                data.patientEncounterId,
            );
        const { billingData, scheduledDate, patientClaim, patient } = patientEncounterDetails;
            

        if (!billingData) {
            showError(
                "Failed to get invoice related insurance type, please refresh the page and try again and contact support if the issue persists.",
            );
            return;
        }

        const latestInvoiceBillingData = billingData.find(
            (bd) => bd.isLatestInvoiceData,
        );

        data.insuranceType = latestInvoiceBillingData?.insuranceType;

        const insuranceTypeIsPrivateOrOverseas = [
            DirectInsuranceType.Private,
            DirectInsuranceType.Overseascover,
        ].includes(data.insuranceType);
        const patientPaysTotalFee =
            latestInvoiceBillingData?.patientPaysTotalFee;
        const isPostPay = latestInvoiceBillingData?.oopType == 1;
        const scheduledDateIsValidAndInPast =
            isDateAndTimeInPast(scheduledDate);
        const isPatientClaimOff = !patientClaim;
        const hasPatientPaymentReceived =
            !!data.payments?.length &&
            data.payments.some(
                (payment) =>
                    payment.paidBy == 0 &&
                    payment.status !== 4 &&
                    [1, 2].includes(payment.status)
                ,
            );

        const isNotRefundOrTopUpInvoice = data.invoiceReason !== 3 && data.invoiceReason !== 4;

        sendInvoiceToPatientModalOpen = true;

        selectedInvoiceData = {
            ...data,
            encounter: patientEncounterDetails,
            billingCodesType: latestInvoiceBillingData.billingCodesType,
            mbsData: latestInvoiceBillingData.mbsData,
            asaData: latestInvoiceBillingData.asaData,
            type: "invoice",
            isFromTable: true,
            patientContacts: patient?.patientContacts ?? [],
            sendLatestReceipt:
                insuranceTypeIsPrivateOrOverseas &&
                patientPaysTotalFee &&
                isPostPay &&
                scheduledDateIsValidAndInPast &&
                isPatientClaimOff &&
                hasPatientPaymentReceived &&
                isNotRefundOrTopUpInvoice,
        };
    };

    const _closeSendInvoiceToPatientModal = () => {
        sendInvoiceToPatientModalOpen = false;
        selectedInvoiceData = {};
    };

    let paperProcessingFee,
        estimateProcessingFee,
        hasInternalProcessingFee = false;

    $: isValidPaperProcessingFee =
        paperProcessingFee >= 0 && paperProcessingFee <= MaxPaperProcessingFee;
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 items-start flex-wrap {$isMobile
                ? 'gap-y-4 flex-col-reverse'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div
                class="flex flex-row items-end justify-start flex-wrap gap-4 flex-1"
            >
                <SearchInput
                    bind:value={filters.id}
                    title="Search Invoice ID"
                    inputFunc={handleFilterSelect}
                />

                <SearchInput
                    bind:value={filters.invoiceNumber}
                    title="Search Invoice No."
                    placeholder="INV"
                    inputFunc={handleSearchInvoiceNo}
                />

                {#if isUserEnableViewDoctor}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />
                {/if}

                <SearchInput
                    bind:value={filters.patientEncounterID}
                    title="Patient Encounter ID"
                    type="number"
                    inputFunc={handleFilterSelect}
                />

                <SearchSelect
                    bind:value={filters.status}
                    title="Status"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value="1,2" selected> All except cancelled </option>
                    {#each invoiceStatus as { id, type }}
                        <option value={id} selected={id == filters.status}>
                            {type}
                        </option>
                    {/each}
                </SearchSelect>

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={clearFilterDoctor}
                />
            </div>

            {#if isUserAdmin}
                <Button clickFunc={openPostCSVModal}>
                    Upload EFT Payments CSV
                </Button>
            {/if}
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingInvoices}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                id="invoiceTableContainer"
            >
                <SvelteTable
                    tableContainerId="invoiceTableContainer"
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnInvoicesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedInvoices.length}
        tableData={returnInvoicesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnInvoicesArr,
            fileName: "Quotes",
            allDataApi: async () =>
                await apiMethods.getMethod.getInvoices(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />

    <PostCsvFileModal {isPostCSVModalOpen} closeModal={closePostCSVModal} />
</div>

{#if isDownloadingInvoice}
    <LoadingModal titleText="Downloading Invoice PDF..." />
{/if}

{#if isRegeneratingInvoice}
    <LoadingModal titleText="Regenerating Invoice PDF..." />
{/if}

<NotificationModal
    isModalOpen={isSetInvoicePaperModalOpen}
    title="Get paper invoice"
    cancelText="Cancel"
    submitText={isSettingInvoicePaper ? "Confirming" : "Confirm"}
    closeModal={closeSetInvoicePaperModal}
    confirmAction={setInvoicePaper}
    isConfirming={isSettingInvoicePaper}
    closeModalFunction={closeSetInvoicePaperModal}
    isConfirmDisabled={!isValidPaperProcessingFee}
    modalMaxWidth="max-w-xl"
>
    <div class="flex gap-x-4 items-center">
        {#if hasInternalProcessingFee}
            Change the processing fee to
        {:else}
            Add
        {/if}
        $
        <DecimalInput
            bind:value={paperProcessingFee}
            inputMax={MaxPaperProcessingFee}
            inputClass={isValidPaperProcessingFee ? "w-16" : "input-error w-16"}
        />
        {#if !hasInternalProcessingFee}
            processing fee to the quote and download.
        {/if}
    </div>
    {#if paperProcessingFee > MaxPaperProcessingFee}
        <p class="text-error mt-5">
            The maximum allowed paper processing fee is ${MaxPaperProcessingFee}.
        </p>
    {/if}
    {#if estimateProcessingFee}
        <p class="text-secondary mt-5">
            Please note this does not include the ${estimateProcessingFee} quote
            processing fee.
        </p>
    {/if}
</NotificationModal>

<SendQuoteOrInvoiceToPatientModal
    data={selectedInvoiceData}
    paymentsData={selectedInvoiceData?.payments}
    bind:isModalOpen={sendInvoiceToPatientModalOpen}
    afterSendFunc={_closeSendInvoiceToPatientModal}
/>
